import { toast } from "sonner";

/**
 * Detects if an error message leaks internal stack traces, database schema,
 * or serialized ASTs/JSON.
 */
export function looksLikeInternalError(msg: string): boolean {
  if (!msg) return false;
  const trimmed = msg.trim();

  // Serialized JSON array/object (e.g. raw Valibot/Zod issue lists)
  if (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  ) {
    return true;
  }

  // Database / internal stack indicators
  const internalPatterns = [
    /\b(postgres|postgresql|drizzle|sqlite|neon|mysql)\b/i,
    /\b(syntax error at or near|violates foreign key|duplicate key value|relation does not exist)\b/i,
    /\b(at\s+[\w$./\\-]+\s+\([\w$./\\-]+:\d+:\d+\))/i, // stack trace lines
    /\b(node_modules|webpack|vite|rolldown)\b/i,
    /\bTRPCClientError:\b/,
    /\b\[object Object\]\b/,
  ];

  return internalPatterns.some((p) => p.test(msg));
}

/**
 * Extracts a human-friendly, safe error message from any tRPC or standard error.
 * Prevents internal server leaks, unparsed JSON arrays, and SQL errors from reaching the user.
 */
export function formatTrpcErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred. Please try again.",
): string {
  if (!error) return fallback;

  if (typeof error === "object" && error !== null) {
    const errObj = error as Record<string, unknown>;
    const data = errObj.data as Record<string, unknown> | undefined;
    const code = data?.code as string | undefined;

    if (code === "INTERNAL_SERVER_ERROR") {
      return "An unexpected server error occurred. Please try again.";
    }
    if (code === "UNAUTHORIZED" || isAuthError(error)) {
      return "Session expired. Please sign in again.";
    }
    if (code === "FORBIDDEN") {
      const msg = typeof errObj.message === "string" ? errObj.message : "";
      return msg && !looksLikeInternalError(msg)
        ? msg
        : "You do not have permission to perform this action.";
    }
    if (code === "NOT_FOUND") {
      const msg = typeof errObj.message === "string" ? errObj.message : "";
      return msg && !looksLikeInternalError(msg)
        ? msg
        : "The requested resource was not found.";
    }

    const message =
      typeof errObj.message === "string"
        ? errObj.message
        : typeof errObj.error === "string"
          ? errObj.error
          : undefined;

    if (message) {
      if (
        message.includes("Failed to fetch") ||
        message.includes("NetworkError") ||
        message.includes("Load failed")
      ) {
        return "Network connection error. Check your connection and try again.";
      }
      if (looksLikeInternalError(message)) {
        return fallback;
      }
      return message;
    }
  }

  if (typeof error === "string") {
    if (looksLikeInternalError(error)) return fallback;
    return error;
  }

  return fallback;
}

/** Check if a tRPC error is a LIMIT_EXCEEDED error and show usage toast. */
export function handleLimitExceededError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const data =
    "data" in error
      ? (
          error as {
            data?: {
              limitExceeded?: { resource: string; used: number; limit: number };
            };
          }
        ).data
      : undefined;

  const msg = "message" in error ? (error as { message: string }).message : "";

  if (data?.limitExceeded || msg.includes("Upgrade to add more")) {
    toast.error(msg, {
      action: {
        label: "View usage",
        onClick: () => {
          window.location.href = "/account#section-billing";
        },
      },
      duration: 8000,
    });
    return true;
  }
  return false;
}

/**
 * Detect if an error is an authentication error (expired session, 401, missing credentials).
 * Handles tRPC errors ({ data: { code: "UNAUTHORIZED" } }), raw HTTP 401 responses,
 * and standard error messages from API middleware.
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as Record<string, unknown>;
  const data = err.data as Record<string, unknown> | undefined;
  const shape = err.shape as Record<string, unknown> | undefined;
  const meta = err.meta as Record<string, unknown> | undefined;
  const response = meta?.response as { status?: number } | undefined;

  if (data?.code === "UNAUTHORIZED" || shape?.code === "UNAUTHORIZED") {
    return true;
  }

  const httpStatus =
    (typeof data?.httpStatus === "number" && data.httpStatus) ||
    (typeof shape?.httpStatus === "number" && shape.httpStatus) ||
    (typeof response?.status === "number" && response.status) ||
    (typeof err.status === "number" && err.status) ||
    (typeof err.statusCode === "number" && err.statusCode);

  if (httpStatus === 401) {
    return true;
  }

  const message =
    typeof err.message === "string"
      ? err.message
      : typeof err.error === "string"
        ? err.error
        : "";

  if (
    message === "Authentication required" ||
    message === "Invalid or expired token" ||
    message === "Session expired. Please sign in again."
  ) {
    return true;
  }

  return false;
}

let isRedirectingToLogin = false;

/** For testing purposes to reset the single-flight redirect guard. */
export function _resetAuthRedirectForTest(): void {
  isRedirectingToLogin = false;
}

/** Redirect to login on auth error, preserving current route and query params. */
export function handleAuthError(error: unknown): boolean {
  if (!isAuthError(error)) return false;

  if (isRedirectingToLogin) return true;
  isRedirectingToLogin = true;

  toast.error("Session expired. Please sign in again.");

  if (typeof window !== "undefined") {
    const current = window.location.pathname + window.location.search;
    const redirectParam =
      current && current !== "/" && current !== "/login"
        ? `?redirect=${encodeURIComponent(current)}`
        : "";
    window.location.href = `/login${redirectParam}`;
  }
  return true;
}

/**
 * Toast an error safely. Prevents raw ASTs, SQL dumps, and unhandled 500 internals
 * from leaking to user-facing notifications.
 */
export function toastTrpcError(
  error: unknown,
  fallback = "Action failed. Please try again.",
): void {
  if (handleLimitExceededError(error)) return;
  if (handleAuthError(error)) return;

  const msg = formatTrpcErrorMessage(error, fallback);
  toast.error(msg);
}
